import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { officeAPI } from '../../services/api';
import { format } from 'date-fns';
const fmtDate = (d, fmt = 'yyyy/MM/dd HH:mm') => d ? format(new Date(d), fmt) : '—';

const STATUS_LABELS = {
  ticket_issued: { label: 'تذكرة صادرة', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-600' },
  boarded: { label: 'صعد الباص', color: 'bg-blue-100 text-blue-700' },
};

export default function OfficeBookingsPage() {
  const [bookings, setBookings]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [status, setStatus]       = useState('');
  const [date, setDate]           = useState('');
  const [detail, setDetail]       = useState(null);   // booking in drawer
  const [cancelling, setCancelling] = useState(false);
  const limit = 15;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await officeAPI.getBookings({ page, limit, search: search || undefined, status: status || undefined, date: date || undefined });
      setBookings(res.data.data || []);
      setTotal(res.data.meta?.total ?? 0);
    } catch { toast.error('خطأ في تحميل الحجوزات'); }
    finally { setLoading(false); }
  }, [page, search, status, date]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const openDetail = async (b) => {
    try {
      const res = await officeAPI.getBookingById(b.id);
      setDetail(res.data.data);
    } catch { toast.error('خطأ في تحميل التفاصيل'); }
  };

  const cancelBooking = async () => {
    if (!detail) return;
    if (!window.confirm('تأكيد إلغاء هذه التذكرة؟')) return;
    setCancelling(true);
    try {
      await officeAPI.cancelBooking(detail.id);
      toast.success('تم إلغاء الحجز');
      setDetail(null);
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في الإلغاء');
    } finally { setCancelling(false); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div dir="rtl" className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">حجوزات المكتب</h1>
          <p className="text-xs text-gray-500 mt-0.5">{(total ?? 0).toLocaleString()} حجز إجمالي</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border p-4 mb-4 flex flex-wrap gap-3">
        <input
          className="border rounded-xl px-3 py-2 text-sm w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="بحث برقم أو اسم..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="border rounded-xl px-3 py-2 text-sm flex-1 sm:flex-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
        >
          <option value="">كل الحالات</option>
          <option value="ticket_issued">تذكرة صادرة</option>
          <option value="cancelled">ملغي</option>
          <option value="boarded">صعد الباص</option>
        </select>
        <input
          type="date"
          className="border rounded-xl px-3 py-2 text-sm flex-1 sm:flex-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={date}
          onChange={(e) => { setDate(e.target.value); setPage(1); }}
        />
        {(search || status || date) && (
          <button onClick={() => { setSearch(''); setStatus(''); setDate(''); setPage(1); }} className="text-xs text-gray-500 hover:text-gray-800 underline">
            إزالة الفلاتر
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
        ) : bookings.length === 0 ? (
          <div className="py-16 text-center text-gray-400">لا توجد حجوزات</div>
        ) : (
          <>
            {/* Desktop/tablet table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">رقم الحجز</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">المسافر</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">الرحلة</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">المقاعد</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">المبلغ</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">العمولة</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">الحالة</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">التاريخ</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {bookings.map((b) => {
                    const st = STATUS_LABELS[b.status] || { label: b.status, color: 'bg-gray-100 text-gray-600' };
                    return (
                      <tr key={b.id} onClick={() => openDetail(b)} className="hover:bg-gray-50 cursor-pointer transition">
                        <td className="px-4 py-3 font-mono font-bold text-blue-700">{b.bookingNumber}</td>
                        <td className="px-4 py-3 text-gray-800">{b.passengerDetails?.name || b.user?.name || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{b.trip?.fromCity} ← {b.trip?.toCity}<br/><span className="text-gray-400">{fmtDate(b.trip?.departureTime)}</span></td>
                        <td className="px-4 py-3 text-gray-600">{b.seats?.join('، ') || '—'}</td>
                        <td className="px-4 py-3 font-semibold tabular-nums">{parseFloat(b.totalAmount || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-emerald-700 font-semibold tabular-nums">{parseFloat(b.commissionAmount || 0).toLocaleString()}</td>
                        <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span></td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(b.createdAt, 'MM/dd HH:mm')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — same data/tap-to-open-detail as the table. */}
            <div className="md:hidden divide-y">
              {bookings.map((b) => {
                const st = STATUS_LABELS[b.status] || { label: b.status, color: 'bg-gray-100 text-gray-600' };
                return (
                  <div key={b.id} onClick={() => openDetail(b)} className="p-4 space-y-2 active:bg-gray-50 cursor-pointer">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-mono font-bold text-blue-700 text-sm">{b.bookingNumber}</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${st.color}`}>{st.label}</span>
                    </div>
                    <p className="text-sm text-gray-800 font-semibold">{b.passengerDetails?.name || b.user?.name || '—'}</p>
                    <p className="text-xs text-gray-600">{b.trip?.fromCity} ← {b.trip?.toCity} <span className="text-gray-400">· {fmtDate(b.trip?.departureTime)}</span></p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">مقاعد: {b.seats?.join('، ') || '—'}</span>
                      <span className="font-semibold tabular-nums text-gray-800">{parseFloat(b.totalAmount || 0).toLocaleString()} ج.س</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 px-3 py-1.5 rounded-lg border bg-white">
              السابق
            </button>
            <span className="text-sm text-gray-500">{page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 px-3 py-1.5 rounded-lg border bg-white">
              التالي
            </button>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex" dir="rtl">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetail(null)} />
          <div className="relative mr-auto w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-bold text-gray-900 text-lg">تفاصيل الحجز</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">رقم الحجز</p>
                <p className="text-2xl font-mono font-bold text-blue-700">{detail.bookingNumber}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  ['المسافر', detail.passengerDetails?.name || detail.user?.name || '—'],
                  ['الهاتف', detail.passengerDetails?.mobile || detail.user?.phone || '—'],
                  ['من', detail.trip?.fromCity],
                  ['إلى', detail.trip?.toCity],
                  ['تاريخ الرحلة', fmtDate(detail.trip?.departureTime)],
                  ['المقاعد', detail.seats?.join('، ')],
                  ['المبلغ', `${parseFloat(detail.totalAmount || 0).toLocaleString()} ج.س`],
                  ['العمولة', `${parseFloat(detail.commissionAmount || 0).toLocaleString()} ج.س`],
                ].map(([k, v]) => (
                  <div key={k} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">{k}</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{v || '—'}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${STATUS_LABELS[detail.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                  {STATUS_LABELS[detail.status]?.label || detail.status}
                </span>
                <span className="text-xs text-gray-400">{fmtDate(detail.createdAt)}</span>
              </div>

              {detail.status === 'ticket_issued' && (
                <button
                  onClick={cancelBooking}
                  disabled={cancelling}
                  className="w-full border-2 border-red-200 text-red-600 py-3 rounded-xl font-bold hover:bg-red-50 disabled:opacity-50 transition"
                >
                  {cancelling ? 'جاري الإلغاء...' : 'إلغاء الحجز'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
